import { useCallback, useMemo, useState } from 'react';

export interface UseDropzoneOptions<E extends HTMLElement = HTMLDivElement> {
  disabled?: boolean;
  onDragStart?(): void;
  onDragEnd?(): void;
  onDrop?(e: React.DragEvent<E>): void;
}

export function useDropzone<E extends HTMLElement = HTMLDivElement>(
  options: UseDropzoneOptions<E> = {}
) {
  const disabled = options.disabled ?? false;

  const [dragging, setDragging] = useState(false);

  const onDragStart = useCallback(
    (event: React.DragEvent<E>) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;
      if (!dragging) {
        options.onDragStart?.();
      }
      setDragging(true);
    },
    [disabled, dragging, options.onDragStart]
  );

  const onDragEnd = useCallback(
    (event: React.DragEvent<E>) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;
      options.onDragEnd?.();
      setDragging(false);
    },
    [disabled, options.onDragEnd]
  );

  const onDrop = useCallback(
    (event: React.DragEvent<E>) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;
      options.onDrop?.(event);
      setDragging(false);
    },
    [disabled, options.onDrop]
  );

  return useMemo(
    () => ({ dragging, onDragStart, onDragEnd, onDrop }),
    [dragging, onDragStart, onDragEnd, onDrop]
  );
}
